import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { BRAND, NAV_ITEMS, SERVICES } from "../data/brand";
import WhatsAppIcon from "./WhatsAppIcon";

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-[#0a0f1f] text-white pt-24 pb-12">
      <div className="container-90">
        {/* Top */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          {/* Brand column */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={BRAND.logo}
                alt="90 Brokers Srls"
                className="h-16 w-auto"
                style={{ filter: "url(#remove-black) brightness(1.4)" }}
              />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-4 max-w-sm leading-tight">
              Consulenza assicurativa{" "}
              <span className="text-[color:var(--brand-blue-light)]">indipendente</span>
              <br /> per chi non si accontenta.
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              Confrontiamo le migliori compagnie del mercato per offrirti la soluzione
              realmente adatta alla tua vita, alla tua famiglia o alla tua impresa.
            </p>

            <div className="flex gap-3 mt-8">
              {BRAND.social.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.network.toLowerCase()}-${i}-link`}
                  className="w-11 h-11 flex items-center justify-center border border-white/20 hover:bg-[color:var(--brand-yellow)] hover:border-[color:var(--brand-yellow)] hover:text-[#0a0f1f] transition-colors"
                  aria-label={`${s.network} ${s.handle}`}
                >
                  {s.network === "Instagram" ? (
                    <Instagram className="w-5 h-5" />
                  ) : (
                    <Facebook className="w-5 h-5" />
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-3">
            <div className="overline text-white/50 mb-5">Pagine</div>
            <ul className="space-y-3">
              {NAV_ITEMS.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.to === "/" ? "home" : n.to.replace("/", "")}-link`}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="overline text-white/50 mb-5 mt-10">Servizi</div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/servizi#${s.id}`}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="overline text-white/50 mb-5">Contatti</div>
            <ul className="space-y-5">
              <li className="flex items-start gap-3" data-testid="footer-address">
                <MapPin className="w-5 h-5 text-[color:var(--brand-blue-light)] flex-shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm leading-relaxed">
                  {BRAND.address.street}
                  <br />
                  03100 {BRAND.address.city} (FR)
                </span>
              </li>
              <li>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  data-testid="footer-phone-link"
                  className="flex items-start gap-3 text-slate-200 text-sm hover:text-white transition-colors group"
                >
                  <Phone className="w-5 h-5 text-[color:var(--brand-blue-light)] flex-shrink-0 mt-0.5" />
                  <span className="font-heading text-base">{BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-start gap-3 text-slate-200 text-sm hover:text-white transition-colors group"
                >
                  <WhatsAppIcon className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-0.5" />
                  <span className="font-heading text-base">WhatsApp · {BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="footer-email-link"
                  className="flex items-start gap-3 text-slate-200 text-sm hover:text-white transition-colors break-all"
                >
                  <Mail className="w-5 h-5 text-[color:var(--brand-blue-light)] flex-shrink-0 mt-0.5" />
                  <span>{BRAND.email}</span>
                </a>
              </li>
            </ul>

            <Link
              to="/contatti"
              data-testid="footer-cta-btn"
              className="inline-flex items-center gap-2 mt-8 text-[color:var(--brand-yellow)] font-heading font-semibold text-sm border-b-2 border-[color:var(--brand-yellow)]/40 pb-1 hover:border-[color:var(--brand-yellow)] transition-colors"
            >
              Parla con un consulente <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Legal compliance block (IVASS) */}
        <div
          data-testid="footer-legal-block"
          className="pt-10 mt-2 border-t border-white/10 text-xs text-slate-400 leading-relaxed space-y-3"
        >
          <p className="text-slate-300">
            <span className="font-heading font-bold text-white tracking-tight">
              {BRAND.name.toUpperCase()}
            </span>{" "}
            <span className="text-slate-500 mx-1">·</span> {BRAND.address.street},{" "}
            {BRAND.address.city} (FR){" "}
            <span className="text-slate-500 mx-1">·</span>{" "}
            <span className="text-slate-400">Partita IVA</span>{" "}
            <span className="text-slate-200 font-semibold">{BRAND.vatNumber}</span>{" "}
            <span className="text-slate-500 mx-1">·</span>{" "}
            <span className="text-slate-400">RUI</span>{" "}
            <span className="text-slate-200 font-semibold">{BRAND.rui}</span>
          </p>
          <p>
            <span className="text-slate-400">Responsabile Assicurativo:</span>{" "}
            <span className="text-slate-200 font-semibold">{BRAND.manager.name}</span>{" "}
            <span className="text-slate-500 mx-1">·</span>{" "}
            <span className="text-slate-400">RUI</span>{" "}
            <span className="text-slate-200 font-semibold">{BRAND.manager.rui}</span>
          </p>
          <p className="flex flex-col sm:flex-row sm:gap-x-6 gap-y-1">
            <span>
              <span className="text-slate-400">Mail:</span>{" "}
              <a
                href={`mailto:${BRAND.email}`}
                className="text-slate-200 hover:text-[color:var(--brand-yellow)] transition-colors"
              >
                {BRAND.email}
              </a>
            </span>
            <span>
              <span className="text-slate-400">PEC:</span>{" "}
              <a
                href={`mailto:${BRAND.pec}`}
                className="text-slate-200 hover:text-[color:var(--brand-yellow)] transition-colors"
              >
                {BRAND.pec}
              </a>
            </span>
          </p>
          <p className="text-slate-300">
            Intermediario soggetto al controllo dell'
            <span className="font-semibold text-white">IVASS</span>.
          </p>
          <p>
            <span className="text-slate-400">Indirizzo di verifica RUI:</span>{" "}
            <a
              href={BRAND.ruiVerifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-rui-link"
              className="text-slate-200 hover:text-[color:var(--brand-yellow)] underline-offset-2 hover:underline break-all"
            >
              {BRAND.ruiVerifyUrl}
            </a>
          </p>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-8 mt-8 border-t border-white/5 gap-4">
          <p className="text-xs text-slate-500">
            © {year} {BRAND.name} · Tutti i diritti riservati
          </p>
          <p className="text-xs text-slate-500">
            Iscritto al RUI — Registro Unico degli Intermediari Assicurativi
          </p>
        </div>
      </div>
    </footer>
  );
}
